import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { getResellers, getClientAdminsByReseller, getClientUsersByClientAdmin } from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import { toast } from 'react-toastify';
import Pagination from '../../../Utils/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import SuperadminConfig from '../../../../config/Superadmin';

const ResellerHierarchy = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);

  // ── Pull everything from config ───────────
  const cfg          = SuperadminConfig.resellers;
  const routes       = SuperadminConfig.routes;
  const clientsCfg   = SuperadminConfig.clients;
  const reportsCfg   = SuperadminConfig.reports;
  const navCfg       = SuperadminConfig.nav;
  const featureFlags = SuperadminConfig.features;

  // ── Theme ──
  const fontFamily    = theme.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize  = theme.typography?.bodyFontSize  || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  // ── Role / basePath ───────
  const currentUser  = JSON.parse(localStorage.getItem('user') || '{}') || {};
  const isSuperadmin = (currentUser.role || '').toLowerCase() === 'superadmin';
  const basePath     = isSuperadmin ? routes.base : '/admin';

  // Back-to-list path — e.g. '/superadmin/advisors'
  const listPath = `${basePath}/${routes.resellers}`;

  // ── Data state ────────────
  const [resellers,            setResellers]            = useState([]);
  const [clientAdmins,         setClientAdmins]         = useState({});
  const [clientUsers,          setClientUsers]          = useState({});
  const [selectedResellerId,   setSelectedResellerId]   = useState(null);
  const [selectedClientAdminId,setSelectedClientAdminId]= useState(null);
  const [loading,              setLoading]              = useState(false);
  const [loadingClientAdmins,  setLoadingClientAdmins]  = useState(false);
  const [loadingClientUsers,   setLoadingClientUsers]   = useState(false);
  const [error,                setError]                = useState('');
  const [currentPage,          setCurrentPage]          = useState(1);
  const [perPage,              setPerPage]              = useState(10);
  const [gotoPage,             setGotoPage]             = useState('');
  const [sortOrderResellers,   setSortOrderResellers]   = useState('asc');
  const [sortOrderClientAdmins,setSortOrderClientAdmins]= useState('asc');
  const [sortOrderClientUsers, setSortOrderClientUsers] = useState('asc');

  const currentClientAdmins = clientAdmins[selectedResellerId]    || [];
  const currentClientUsers  = clientUsers[selectedClientAdminId]  || [];

  // ── Top nav state ─────────
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const [isClientsPinned,       setIsClientsPinned]       = useState(false);
  const [isReportsPinned,       setIsReportsPinned]       = useState(false);
  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);
  const activeTopTab = 'resellers';
  const navigate = useNavigate();

  // ── Admin-only tab list ───
  const ALL_ADMIN_TABS = ['resellers', 'clients', 'reports', 'account'];
  const adminTabs = ALL_ADMIN_TABS.filter((t) => {
    if (featureFlags?.tabs) return featureFlags.tabs[t] !== false;
    return true;
  });

  // ── Top nav handlers ──────
  const handleTopTabClick = (tab) => {
    if (tab === 'clients') {
      setIsClientsPinned((p) => !p);
      setIsClientsDropdownOpen((p) => !p);
      setIsReportsPinned(false);
      setIsReportsDropdownOpen(false);
      return;
    }
    if (tab === 'reports') {
      setIsReportsPinned((p) => !p);
      setIsReportsDropdownOpen((p) => !p);
      setIsClientsPinned(false);
      setIsClientsDropdownOpen(false);
      return;
    }
    setIsClientsPinned(false); setIsClientsDropdownOpen(false);
    setIsReportsPinned(false); setIsReportsDropdownOpen(false);
    const map = {
      resellers : listPath,
      notify    : `${basePath}/${routes.notify}`,
      admins    : `${basePath}/${routes.admins}`,
      account   : `${basePath}/${routes.account}`,
    };
    if (map[tab]) navigate(map[tab]);
  };

  const handleClientSubTab = (subTab) => {
    navigate(
      subTab === 'enterprise'
        ? `${basePath}/${routes.clients.enterprise}`
        : `${basePath}/${routes.clients.core}`
    );
    setIsClientsDropdownOpen(false); setIsClientsPinned(false);
  };

  const handleReportSubTab = (subTab) => {
    navigate(`${basePath}/${routes.reports.base}?sub=${subTab}`);
    setIsReportsDropdownOpen(false); setIsReportsPinned(false);
  };

  // ── Outside click handler ─
  useEffect(() => {
    const handle = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target)) {
        setIsClientsDropdownOpen(false); setIsClientsPinned(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false); setIsReportsPinned(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Fetch resellers on mount ──
  useEffect(() => {
    const fetchResellers = async () => {
      setLoading(true);
      try {
        const response = await getResellers();
        const fetched  = Array.isArray(response.data.data) ? response.data.data : [];
        setResellers(fetched);
        if (fetched.length === 0) setError(`No ${cfg.plural.toLowerCase()} found.`);
      } catch {
        setError(`Failed to fetch ${cfg.plural.toLowerCase()}. Please try again.`);
      } finally { setLoading(false); }
    };
    fetchResellers();
  }, []);

  // ── Fetch client admins ───
  const fetchClientAdmins = async (resellerId) => {
    setLoadingClientAdmins(true);
    try {
      const response = await getClientAdminsByReseller(resellerId);
      const admins   = Array.isArray(response.data.data) ? response.data.data : [];
      setClientAdmins((prev) => ({ ...prev, [resellerId]: admins }));
      if (admins.length === 0) toast.info(`No ${clientsCfg.core.label.toLowerCase()} found for this ${cfg.singular.toLowerCase()}.`);
    } catch {
      toast.error(`Failed to fetch ${clientsCfg.core.label.toLowerCase()}.`);
      setClientAdmins((prev) => ({ ...prev, [resellerId]: [] }));
    } finally { setLoadingClientAdmins(false); }
  };

  // ── Fetch client users ────
  const fetchClientUsers = async (clientAdminId) => {
    setLoadingClientUsers(true);
    try {
      const response = await getClientUsersByClientAdmin(clientAdminId);
      const users    = Array.isArray(response.data.data) ? response.data.data : [];
      setClientUsers((prev) => ({ ...prev, [clientAdminId]: users }));
      if (users.length === 0) toast.info(displayText('No client users found for this client admin.'));
    } catch {
      toast.error(displayText('Failed to fetch client users.'));
      setClientUsers((prev) => ({ ...prev, [clientAdminId]: [] }));
    } finally { setLoadingClientUsers(false); }
  };

  const handleResellerClick = (resellerId) => {
    if (selectedResellerId === resellerId) { setSelectedResellerId(null); setSelectedClientAdminId(null); return; }
    setSelectedResellerId(resellerId); setSelectedClientAdminId(null);
    if (!clientAdmins[resellerId]) fetchClientAdmins(resellerId);
  };

  const handleClientAdminClick = (clientAdminId) => {
    if (selectedClientAdminId === clientAdminId) { setSelectedClientAdminId(null); return; }
    setSelectedClientAdminId(clientAdminId);
    if (!clientUsers[clientAdminId]) fetchClientUsers(clientAdminId);
  };

  // ── Sorting ─
  const sortedResellers = [...resellers].sort((a, b) => {
    const nameA = a.reseller_name.toLowerCase();
    const nameB = b.reseller_name.toLowerCase();
    return sortOrderResellers === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });

  const sortedClientAdmins = [...currentClientAdmins].sort((a, b) => {
    const nameA = (a.client_name || a.client_admin_name || `${a.first_name || ''} ${a.last_name || ''}`.trim()).toLowerCase();
    const nameB = (b.client_name || b.client_admin_name || `${b.first_name || ''} ${b.last_name || ''}`.trim()).toLowerCase();
    return sortOrderClientAdmins === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });

  const sortedClientUsers = [...currentClientUsers].sort((a, b) => {
    const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim()).toLowerCase();
    const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim()).toLowerCase();
    return sortOrderClientUsers === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });

  const paginatedResellers = sortedResellers.slice((currentPage - 1) * perPage, currentPage * perPage);

  // ── Shared styles ─────────
  const thStyle = { padding: '0.5rem 1rem', textAlign: 'left', fontSize: `${bodyFontSize}px`, fontFamily };
  const tdStyle = { padding: '0.5rem 1rem', textAlign: 'left', fontSize: `${bodyFontSize}px`, fontFamily };
  const hrStyle = { margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}` };

  const getDropdownItemStyle = () => ({
    width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
    background: 'transparent', color: theme.colors.textPrimary,
    border: 'none', cursor: 'pointer',
    fontSize: `${bodyFontSize}px`, fontFamily,
    transition: 'all 0.2s ease',
  });

  // ── Render ──
  return (
    <div style={{
      fontFamily, fontSize: `${bodyFontSize}px`, width: '100%', boxSizing: 'border-box',
      backgroundColor: theme.colors.bgPrimary, color: theme.colors.textPrimary,
      maxWidth: '100%', margin: '0 auto', textAlign: 'initial',
    }}>

      {/* ── TOP NAV — only for non-superadmin ── */}
      {!isSuperadmin && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center', flexWrap: 'wrap', fontFamily }}>
          {adminTabs.map((tab) => {
            const isClientsTab = tab === 'clients';
            const isReportsTab = tab === 'reports';
            const hasDropdown  = isClientsTab || isReportsTab;
            const tabLabel     = navCfg[tab]?.label || (tab.charAt(0).toUpperCase() + tab.slice(1));
            return (
              <div
                key={tab}
                style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
                ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
                onMouseEnter={() => {
                  if (isClientsTab) setIsClientsDropdownOpen(true);
                  if (isReportsTab) setIsReportsDropdownOpen(true);
                }}
                onMouseLeave={() => {
                  if (isClientsTab && !isClientsPinned) setIsClientsDropdownOpen(false);
                  if (isReportsTab && !isReportsPinned) setIsReportsDropdownOpen(false);
                }}
              >
                <button
                  onClick={() => handleTopTabClick(tab)}
                  style={{
                    width: '100%', padding: '0.5rem', borderRadius: '6px', border: 'none',
                    fontWeight: 'bold',
                    background: tab === activeTopTab ? theme.gradients.primary : theme.gradients.secondary,
                    color: '#fff', cursor: 'pointer', fontFamily,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  }}
                >
                  {navCfg[tab]?.icon && (
                    <i className={`bi bi-${navCfg[tab].icon}`} style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }} />
                  )}
                  <span>{tabLabel}</span>
                  {hasDropdown && <i className="bi bi-chevron-down" style={{ fontSize: '12px' }} />}
                </button>

                {/* Clients dropdown */}
                {isClientsTab && isClientsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '160px', overflow: 'hidden', fontFamily,
                  }}>
                    {featureFlags?.clients?.core !== false && (
                      <button style={getDropdownItemStyle()} onClick={() => handleClientSubTab('core')}
                        onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                        {clientsCfg.core.label}
                      </button>
                    )}
                    {featureFlags?.clients?.core !== false && featureFlags?.clients?.enterprise !== false && (
                      <hr style={hrStyle} />
                    )}
                    {featureFlags?.clients?.enterprise !== false && (
                      <button style={getDropdownItemStyle()} onClick={() => handleClientSubTab('enterprise')}
                        onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                        {clientsCfg.enterprise.label}
                      </button>
                    )}
                  </div>
                )}

                {/* Reports dropdown */}
                {isReportsTab && isReportsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '180px', overflow: 'hidden', fontFamily,
                  }}>
                    {Object.entries(reportsCfg)
                      .filter(([key]) => featureFlags?.reports?.[key] !== false)
                      .map(([key, item], idx, arr) => (
                        <React.Fragment key={key}>
                          <button style={getDropdownItemStyle()} onClick={() => handleReportSubTab(key)}
                            onMouseOver={(e) => (e.currentTarget.style.background = theme.gradients.secondary)}
                            onMouseOut={(e)  => (e.currentTarget.style.background = 'transparent')}>
                            {item.label}
                          </button>
                          {idx < arr.length - 1 && <hr style={hrStyle} />}
                        </React.Fragment>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Breadcrumb ──
          Link uses listPath = basePath + routes.resellers → '/superadmin/advisors'
          Clicking this navigates back to the Advisors list, NOT closes/logs out.
      ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <Link to={listPath} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>
            {cfg.plural} Management
          </Link>
          <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
          <span style={{ color: theme.colors.textPrimary }}>{cfg.singular} Hierarchy</span>
        </div>
      </div>

      {/* ── Main Container ── */}
      <div style={{
        backgroundColor: theme.colors.bgPrimary, borderRadius: '12px', padding: '1.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
      }}>
        <h4 style={{ fontSize: `${titleFontSize}px`, fontWeight: 'bold', color: theme.colors.textPrimary, marginBottom: '1rem', textAlign: 'left' }}>
          {cfg.plural} Management
        </h4>

        {error && (
          <div style={{
            padding: '0.75rem 1.25rem', marginBottom: '1rem',
            border: `1px solid ${theme.colors.errorBorder}`, borderRadius: '0.25rem',
            color: theme.colors.errorText, backgroundColor: theme.colors.errorBg,
            textAlign: 'center', fontSize: `${bodyFontSize}px`, fontFamily,
          }}>
            {error}
          </div>
        )}

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={sortedResellers.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />

        {/* ── Hierarchy Table ── */}
        <div style={{
          marginTop: '1rem', marginBottom: '1rem',
          display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row',
          width: '100%', height: '500px', overflowY: 'auto', borderRadius: '10px',
          backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`,
        }}>
          {/* Left Column: Resellers */}
          <div style={{
            width: window.innerWidth < 600 ? '100%' : '33.33%',
            borderRight: window.innerWidth < 600 ? 'none' : `1px solid ${theme.colors.borderColor}`,
            borderBottom: window.innerWidth < 600 ? `1px solid ${theme.colors.borderColor}` : 'none',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite, borderBottom: `1px solid ${theme.colors.borderColor}` }}>
                  <th style={{ ...thStyle, width: '30px' }}>ID</th>
                  <th style={{ ...thStyle, cursor: 'pointer' }}>
                    {cfg.singular} Name{' '}
                    <i className="bi bi-arrow-down" onClick={() => setSortOrderResellers((p) => p === 'asc' ? 'desc' : 'asc')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={2} style={tdStyle}>Loading...</td></tr>
                ) : paginatedResellers.length > 0 ? (
                  paginatedResellers.map((reseller) => (
                    <React.Fragment key={reseller.id}>
                      <tr
                        style={{ cursor: 'pointer', backgroundColor: selectedResellerId === reseller.id ? '#f0f0f0' : '#ffffff' }}
                        onClick={() => handleResellerClick(reseller.id)}
                      >
                        <td style={{ ...tdStyle, width: '30px' }}>{reseller.id}</td>
                        <td style={tdStyle}>{reseller.reseller_name}</td>
                      </tr>
                      <tr><td colSpan={2} style={{ padding: 0 }}><hr style={hrStyle} /></td></tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr><td colSpan={2} style={tdStyle}>No {cfg.plural.toLowerCase()} found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Center Column: Client Admins */}
          <div style={{
            width: window.innerWidth < 600 ? '100%' : '33.33%',
            borderRight: window.innerWidth < 600 ? 'none' : `1px solid ${theme.colors.borderColor}`,
            borderBottom: window.innerWidth < 600 ? `1px solid ${theme.colors.borderColor}` : 'none',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite, borderBottom: `1px solid ${theme.colors.borderColor}` }}>
                  <th style={{ ...thStyle, width: '30px' }}>ID</th>
                  <th style={{ ...thStyle, cursor: 'pointer' }}>
                    {clientsCfg.core.label}{' '}
                    <i className="bi bi-arrow-down" onClick={() => setSortOrderClientAdmins((p) => p === 'asc' ? 'desc' : 'asc')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {!selectedResellerId ? (
                  <tr><td colSpan={2} style={tdStyle}>Select a {cfg.singular.toLowerCase()} to view {clientsCfg.core.label.toLowerCase()}.</td></tr>
                ) : loadingClientAdmins ? (
                  <tr><td colSpan={2} style={tdStyle}>Loading...</td></tr>
                ) : sortedClientAdmins.length > 0 ? (
                  sortedClientAdmins.map((clientAdmin) => (
                    <React.Fragment key={clientAdmin.id}>
                      <tr
                        style={{ cursor: 'pointer', backgroundColor: selectedClientAdminId === clientAdmin.id ? '#e0e0e0' : '#f9f9f9' }}
                        onClick={() => handleClientAdminClick(clientAdmin.id)}
                      >
                        <td style={{ ...tdStyle, width: '30px' }}>{clientAdmin.id}</td>
                        <td style={tdStyle}>
                          {clientAdmin.client_name || clientAdmin.client_admin_name || `${clientAdmin.first_name || ''} ${clientAdmin.last_name || ''}`.trim()}
                        </td>
                      </tr>
                      <tr><td colSpan={2} style={{ padding: 0 }}><hr style={hrStyle} /></td></tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr><td colSpan={2} style={tdStyle}>No {clientsCfg.core.label.toLowerCase()} found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Right Column: Client Users */}
          <div style={{ width: window.innerWidth < 600 ? '100%' : '33.33%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite, borderBottom: `1px solid ${theme.colors.borderColor}` }}>
                  <th style={{ ...thStyle, width: '30px' }}>ID</th>
                  <th style={{ ...thStyle, cursor: 'pointer' }}>{displayText(" Client User")}{' '}
                    <i className="bi bi-arrow-down" onClick={() => setSortOrderClientUsers((p) => p === 'asc' ? 'desc' : 'asc')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {!selectedClientAdminId ? (
                  <tr><td colSpan={2} style={tdStyle}>Select a {clientsCfg.core.label.toLowerCase()}{displayText(" to view client users.")}</td></tr>
                ) : loadingClientUsers ? (
                  <tr><td colSpan={2} style={tdStyle}>Loading...</td></tr>
                ) : sortedClientUsers.length > 0 ? (
                  sortedClientUsers.map((clientUser) => (
                    <React.Fragment key={clientUser.id}>
                      <tr style={{ backgroundColor: '#ffffff' }}>
                        <td style={{ ...tdStyle, width: '30px' }}>{clientUser.id}</td>
                        <td style={tdStyle}>
                          {clientUser.name || `${clientUser.first_name || ''} ${clientUser.last_name || ''}`.trim()}
                        </td>
                      </tr>
                      <tr><td colSpan={2} style={{ padding: 0 }}><hr style={hrStyle} /></td></tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr><td colSpan={2} style={tdStyle}>{displayText("No client users found.")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={sortedResellers.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />
      </div>
    </div>
  );
};

export default ResellerHierarchy;